import React from "react";

import withStyles from '@mui/styles/withStyles';
import classnames from "classnames";

import { ModifiedShipmentTrackingStatus, ShipmentTrackingStatus } from "../../../../util/enums";

type Props = {
  value?: string;
  backgroundColor?: string;
  dataCy?: string;
  textColor?: string;
  classes?: any;
  className?: string;
};

const styles = () => ({
  customBadge: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: (props: { textColor?: string; backgroundColor?: string }) =>
      props?.backgroundColor,
    color: (props: { textColor?: string }) => props?.textColor ?? "",
    minWidth: "51px",
    padding: "8px 12px",
    height: "28px",
    borderRadius: "20px",
  },
  textValue: {
    color: (props: { textColor?: string }) => props?.textColor ?? "",
    fontFamily: "Manrope",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
    letterSpacing: "0.5px",
  },
});

const SimpleBadge = (props: Props) => {
  const { classes, value, className, dataCy } = props;

  return (
    <div className={classnames(classes.customBadge, className)}>
      <span className={classes.textValue} data-cy={dataCy}>
        {value}
      </span>
    </div>
  );
};

const StyledBadge = withStyles(styles)(SimpleBadge);

const BadgeShipmentTrackingStatus = (props: Props) => {
  const { value, className, dataCy } = props;

  const renderBadge = (backgroundColor: string, textColor: string, value?: string) => {
    if (value)
      return (
        <StyledBadge
          dataCy={dataCy}
          backgroundColor={backgroundColor}
          textColor={textColor}
          value={value}
          className={className}
        />
      );

    return <> </>;
  };

  switch (value?.toLowerCase()) {
    case ShipmentTrackingStatus.BOOKED.toLowerCase():
      return renderBadge("#E0F2FE", "#0086C9", value);
    case ShipmentTrackingStatus.COLLECTED_FROM_SHIPPER.toLowerCase():
      return renderBadge("#E0EAFF", "#444CE7", value);
    case ShipmentTrackingStatus.NOT_ARRIVED_AT_PORT_OF_LOAD.toLowerCase():
      return renderBadge("#FEE4E2", "#D92D20", value);
    case ShipmentTrackingStatus.WAITING_AT_PORT_OF_LOAD.toLowerCase():
      return renderBadge("#FEF0C7", "#DC6803", value);
    case ShipmentTrackingStatus.SHIPPED_ON_BOARD.toLowerCase():
      return renderBadge("#FCE7F6", "#DD2590", value);
    case ShipmentTrackingStatus.UNDERWAY.toLowerCase():
      return renderBadge("#F4EBFF", "#42307D", value);
    case ShipmentTrackingStatus.UNDERWAY_TO_A_TRANSHIPMENT_PORT.toLowerCase():
      return renderBadge("#F4EBFF", "#42307D", value);
    case ShipmentTrackingStatus.ARRIVAL_AT_TRANSHIPMENT_PORT.toLowerCase():
      return renderBadge("#EAECF5", "#308CDB", value);
    case ShipmentTrackingStatus.WAITING_AT_TRANSHIPMENT_PORT.toLowerCase():
      return renderBadge("#FEF0C7", "#DC6803", value);
    case ShipmentTrackingStatus.SHIPPED_ON_BOARD_AT_TRANSHIPMENT_PORT.toLowerCase():
      return renderBadge("#FCE7F6", "#851651", value);
    case ShipmentTrackingStatus.ARRIVED_AT_PORT_OR_DISCHARGE.toLowerCase():
    case ModifiedShipmentTrackingStatus.ARRIVED_AT_PORT_OF_DISCHARGE.toLowerCase():
      return renderBadge("#EAECF5", "#308CDB", value);
    case ShipmentTrackingStatus.WAITING_AT_DESTINATION_PORT.toLowerCase():
    case ModifiedShipmentTrackingStatus.WAITING_AT_PORT_OF_DISCHARGE.toLowerCase():
      return renderBadge("#FEF0C7", "#DC6803", value);
    case ShipmentTrackingStatus.LEFT_THE_DESTINATION_PORT.toLowerCase():
    case ModifiedShipmentTrackingStatus.DEPARTED_FROM_PORT_OF_DISCHARGE.toLowerCase():
      return renderBadge("#E0F2FE", "#0B4A6F", value);
    case ShipmentTrackingStatus.SHIPMENT_COMPLETED.toLowerCase():
      return renderBadge("#D1FADF", "#039855", value);
    case ShipmentTrackingStatus.WAITING_FOR_UPDATE.toLowerCase():
      return renderBadge("#E4E7EC", "#475467", value);
    case ShipmentTrackingStatus.TRACKING_NOT_AVAILABLE.toLowerCase():
      return renderBadge("#E4E7EC", "#475467", value);
    default:
      return renderBadge("#eafaf9", "#38CFC4", value);
  }
};

export default BadgeShipmentTrackingStatus;
